import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { formatCurrency } from '../utils/pensionCalc';
import FlagBadge, { rowAccent } from './FlagBadge';

// All noteworthy flags for a run-sheet row, most prominent first. A row can
// legitimately have several things worth flagging at once (e.g. a base change AND
// a bonus), so we surface each as its own badge rather than collapsing to one —
// otherwise a gross addition like a bonus stays hidden behind whichever single
// flag won precedence, and only appears if you expand the row.
export function deriveFlags(row) {
  if (!row) return [];
  const flags = [];
  const status = row.status_this_period;
  const date = row.status_date ? shortDate(row.status_date) : '';
  switch (status) {
    case 'new_starter':
      flags.push({ kind: 'starter', label: date ? `New Starter — ${date}` : 'New Starter' });
      break;
    case 'leaver':
      flags.push({ kind: 'leaver', label: date ? `Leaver — last day ${date}` : 'Leaver' });
      break;
    case 'future_leaver':
      flags.push({ kind: 'leaver', label: date ? `Leaving ${date}` : 'Leaving (future)' });
      break;
    case 'recently_joined':
      flags.push({ kind: 'recent', label: date ? `Started ${date} — check BrightPay` : 'Recently joined' });
      break;
    case 'joined_not_processed':
      flags.push({ kind: 'backpay', label: date ? `Started ${date} — not yet in payroll` : 'Not yet in payroll' });
      break;
    default:
      break;
  }

  if (row.pension_record?.scheme_type_missing) {
    flags.push({ kind: 'warn', label: 'Pension scheme?' });
  }
  if (row.base_changed_from_previous) {
    flags.push({
      kind: 'change',
      label: row.base_change_effective_date
        ? `Base changed — eff ${shortDate(row.base_change_effective_date)}`
        : 'Base changed',
    });
  }

  // Gross additions (bonus, commission, other one-off or recurring additions).
  // These inflate the Gross Pay figure but are otherwise indistinguishable from
  // base pay in the table, so flag them explicitly with the amount. Holiday pay
  // has its own dedicated handling and is excluded here.
  const grossAdds = (row.adjustments || []).filter(
    a => a.category === 'gross_add' && a.source !== 'holiday_pay'
  );
  if (grossAdds.length > 0) {
    const total = grossAdds.reduce((s, a) => s + (Number(a.amount) || 0), 0);
    const label = grossAdds.length === 1
      ? `${grossAdds[0].label} +${formatCurrency(grossAdds[0].amount)}`
      : `${grossAdds.length} additions +${formatCurrency(total)}`;
    flags.push({ kind: 'addition', label });
  }

  return flags;
}

// Single primary flag for a row — drives the accent bar/background and auto-expand.
// Precedence is unchanged (status → pension → base change → addition), so existing
// row colours stay the same; additions surface as an extra badge alongside.
export function deriveFlag(row) {
  return deriveFlags(row)[0] || null;
}

function shortDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

const DENSITY = {
  compact: { rowH: 38, font: 13 },
  cozy:    { rowH: 48, font: 14.5 },
  roomy:   { rowH: 60, font: 15.5 },
};

function Check({ done, onClick, disabled, size = 22 }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`grid place-items-center rounded-md transition-colors ${done ? 'bg-emerald-500' : 'bg-gray-200 hover:bg-gray-300'} ${disabled ? 'cursor-default opacity-90' : 'cursor-pointer'}`}
      style={{ width: size, height: size }}
      aria-label={done ? 'Mark not entered' : 'Mark entered'}
    >
      {done && (
        <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </button>
  );
}

// SummaryTable — the payroll-entry summary at the bottom of the submission detail.
// Pure presentation: flag derivation, accent bar, zebra striping, density, expand-on-click,
// done-check, period totals row. Rich expansion content is rendered by `renderExpansion(row)`.
export default function SummaryTable({
  rows = [],
  total = 0,
  preTaxTotal = null,
  postTaxTotal = null,
  density = 'cozy',
  isChecked,           // (row) => boolean
  onToggleCheck,       // (row) => void
  checksDisabled = false,
  renderExpansion,     // (row) => ReactNode
  flagFilter = null,   // Set<string> of flag.kind to show only; null = show all
  expandAllSignal = 0, // bump to force-expand-all
  collapseAllSignal = 0, // bump to force-collapse-all
}) {
  const dims = DENSITY[density] || DENSITY.cozy;

  const rowsWithFlags = useMemo(
    () => rows.map(r => {
      const flags = deriveFlags(r);
      return { ...r, _flags: flags, _flag: flags[0] || null };
    }),
    [rows]
  );

  // Auto-open only rows that actually have something worth showing: a visible flag,
  // or the backend's `has_interest` signal (mid-period changes, gross adds, holiday
  // pay, hourly breakdown, etc.). A bare salary row with no adjustments stays closed.
  const initialExpanded = useMemo(() => {
    const s = new Set();
    rowsWithFlags.forEach(r => {
      if (r._flag || r.has_interest) s.add(r.employee_id || r.id);
    });
    return s;
  }, [rowsWithFlags]);

  const [expanded, setExpanded] = useState(initialExpanded);

  // The run sheet reloads after every edit (an adjustment, a holiday override, a
  // pro-rata switch), so the auto-open default applies only to a row the first time
  // it appears. Re-seeding on every reload reopened every flagged row the admin had
  // just closed. A different submission remounts this table via its `key`.
  const seenKeys = useRef(new Set());
  useEffect(() => {
    const fresh = rowsWithFlags
      .map(r => r.employee_id || r.id)
      .filter(k => !seenKeys.current.has(k));
    if (fresh.length === 0) return;
    fresh.forEach(k => seenKeys.current.add(k));
    setExpanded(prev => {
      const next = new Set(prev);
      fresh.forEach(k => { if (initialExpanded.has(k)) next.add(k); });
      return next;
    });
  }, [rowsWithFlags, initialExpanded]);

  useEffect(() => {
    if (expandAllSignal === 0) return;
    setExpanded(new Set(rowsWithFlags.map(r => r.employee_id || r.id)));
  }, [expandAllSignal, rowsWithFlags]);

  useEffect(() => {
    if (collapseAllSignal === 0) return;
    setExpanded(new Set());
  }, [collapseAllSignal]);

  const toggle = useCallback(key => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const visibleRows = useMemo(() => {
    if (!flagFilter || flagFilter.size === 0) return rowsWithFlags;
    return rowsWithFlags.filter(r => (r._flags || []).some(f => flagFilter.has(f.kind)));
  }, [rowsWithFlags, flagFilter]);

  return (
    <div className="bg-white rounded-xl ring-1 ring-gray-200 overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="w-8" aria-hidden="true"></th>
            <th className="w-14 px-2 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Done</th>
            <th className="px-3 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Employee</th>
            <th className="px-3 py-3 text-left text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Flags</th>
            <th className="px-3 py-3 text-right text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Gross Pay</th>
            <th className="px-3 py-3 text-right text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Pre-tax</th>
            <th className="px-3 py-3 text-right text-[13.5px] font-bold uppercase tracking-wider text-gray-600">Post-tax</th>
            <th className="w-3" aria-hidden="true"></th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, i) => {
            const key = row.employee_id || row.id || i;
            const flag = row._flag;
            const accent = rowAccent(flag);
            const stripe = i % 2 === 0 ? 'rgba(245,243,251,0.55)' : 'transparent';
            const checked = isChecked ? !!isChecked(row) : !!row.done;
            const isOpen = expanded.has(key);
            const hasExpansion = !!renderExpansion;
            const preTax = row.pre_tax_deduct_total || 0;
            const postTax = row.post_tax_deduct_total || 0;

            return (
              <React.Fragment key={key}>
                <tr
                  className={`border-b border-gray-100 transition-colors ${hasExpansion ? 'cursor-pointer hover:bg-brand-50/40' : ''}`}
                  style={{ background: flag ? accent.bg : stripe, height: dims.rowH }}
                  onClick={hasExpansion ? () => toggle(key) : undefined}
                >
                  <td className="relative" style={{ width: 8 }}>
                    {flag && (
                      <span
                        aria-hidden="true"
                        style={{
                          position: 'absolute', left: 0, top: 4, bottom: 4, width: 3,
                          background: accent.bar, borderRadius: 2,
                        }}
                      />
                    )}
                    {hasExpansion && (
                      <span
                        className="ml-1.5 text-gray-400 inline-block transition-transform"
                        style={{ transform: isOpen ? 'rotate(90deg)' : 'none' }}
                        aria-hidden="true"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 align-middle" onClick={e => e.stopPropagation()}>
                    <Check
                      done={checked}
                      disabled={checksDisabled}
                      onClick={() => !checksDisabled && onToggleCheck && onToggleCheck(row)}
                    />
                  </td>
                  <td className="px-3 py-2 align-middle" style={{ fontSize: dims.font, lineHeight: 1.3 }}>
                    <span className={`font-semibold ${checked ? 'line-through text-gray-400' : 'text-ink'}`}>
                      {row.employee_name}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-middle">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {(row._flags || []).map((f, fi) => (
                        <FlagBadge key={`${f.kind}-${fi}`} flag={f} />
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 align-middle text-right" style={{ fontSize: dims.font }}>
                    <span className={`tabular font-semibold ${checked ? 'line-through text-gray-400' : 'text-ink'}`}>
                      {formatCurrency(row.gross_pay || 0)}
                    </span>
                  </td>
                  <td className="px-3 py-2 align-middle text-right">
                    {preTax !== 0
                      ? <span className="tabular text-rose-600 font-medium">{formatCurrency(preTax)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 align-middle text-right">
                    {postTax !== 0
                      ? <span className="tabular text-rose-600 font-medium">{formatCurrency(postTax)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td></td>
                </tr>

                {isOpen && hasExpansion && (
                  <tr style={{ background: flag ? accent.bg : '#fbfafd' }}>
                    <td className="relative" aria-hidden="true">
                      {flag && (
                        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: accent.bar }} />
                      )}
                    </td>
                    <td colSpan={7} className="px-3 pt-1 pb-5">
                      <div className="ml-6">{renderExpansion(row)}</div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}

          {visibleRows.length === 0 && (
            <tr>
              <td colSpan={8} className="px-6 py-10 text-center text-[15px] text-gray-500 italic">
                No rows match the active flag filter.
              </td>
            </tr>
          )}

          <tr className="bg-gradient-to-r from-brand-50 to-brand-100/60 border-t-2 border-brand-300">
            <td aria-hidden="true"></td>
            <td aria-hidden="true"></td>
            <td aria-hidden="true"></td>
            <td className="px-3 py-4 text-right">
              <span className="text-[15px] uppercase tracking-wider font-bold text-brand-900">Period totals</span>
            </td>
            <td className="px-3 py-4 text-right">
              <span className="text-[20px] 2xl:text-[24px] font-bold text-brand-900 tabular">{formatCurrency(total)}</span>
            </td>
            <td className="px-3 py-4 text-right">
              {preTaxTotal != null && preTaxTotal !== 0
                ? <span className="text-[17px] tabular text-rose-700 font-semibold">{formatCurrency(preTaxTotal)}</span>
                : <span className="text-gray-300">—</span>}
            </td>
            <td className="px-3 py-4 text-right">
              {postTaxTotal != null && postTaxTotal !== 0
                ? <span className="text-[17px] tabular text-rose-700 font-semibold">{formatCurrency(postTaxTotal)}</span>
                : <span className="text-gray-300">—</span>}
            </td>
            <td aria-hidden="true"></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
